import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';

interface Column {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => string;
}

interface SummaryTableProps {
  data: any[];
  columns: any[];
  loading?: boolean;
}

const SummaryTable = ({ data, columns, loading }: SummaryTableProps) => {
  if (loading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate totals
  const totals = data.reduce(
    (acc, row) => ({
      totalHoursAllocated:
        acc.totalHoursAllocated + (row.totalHoursAllocated || 0),
      totalHoursUtilized:
        acc.totalHoursUtilized + (row.totalHoursUtilized || 0),
    }),
    { totalHoursAllocated: 0, totalHoursUtilized: 0 }
  );

  // Define column widths
  const columnWidths = {
    accountName: '40%',
    totalHoursAllocated: '30%',
    totalHoursUtilized: '30%',
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          overflow: 'auto',
          border: 'none',
          boxShadow: 'none',
        }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    bgcolor: 'white',
                    fontWeight: 'bold',
                    padding: '6px 8px',
                    width: columnWidths[column.id],
                  }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography
                    variant="body2"
                    textAlign="center"
                    color="text.secondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.id}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        padding: '3px 8px',
                        width: columnWidths[column.id],
                      }}>
                      {column.format
                        ? column.format(row[column.id])
                        : row[column.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Separate Total Row */}
      <Box
        sx={{
          borderTop: '1px solid rgba(224, 224, 224, 1)',
          bgcolor: 'white',
          mt: 'auto',
        }}>
        <Table style={{ borderRadius: 1 }}>
          <TableRow
            sx={{
              borderRadius: 1,
              '& td': {
                fontWeight: 'bold',
                padding: '6px 8px',
              },
            }}>
            <TableCell sx={{ width: columnWidths.accountName }}>
              Total
            </TableCell>
            <TableCell
              align="right"
              sx={{ width: columnWidths.totalHoursAllocated }}>
              {totals.totalHoursAllocated.toFixed(2)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ width: columnWidths.totalHoursUtilized }}>
              {totals.totalHoursUtilized.toFixed(2)}
            </TableCell>
          </TableRow>
        </Table>
      </Box>
    </Box>
  );
};

export default SummaryTable;
